import React, { useState, useEffect, useCallback } from 'react';
import { useGetCrawledCuponsQuery } from 'state/crawled-coupons';
import TailwindDataTable from 'scenes/crawled_cupons/components/TailwindDataTable';
import Snackbar from 'components/Snackbar';
import AppBarWithFilters from '../components/AppBar';
import ZyteSection from '../components/ZyteSection';
import ImportCouponsButton from '../components/ImportCouponsButton';
import SyncTrButton from '../components/SyncTrButton';
import getColumns from '../list/columns';
import useFilteredData from '../components/FilteredData';
import { Store, ShoppingBag, Filter, TrendingUp } from 'lucide-react';
import ExportCouponsModal from '../components/ExportCouponsModal';
import TRActionModal from '../components/TRActionModal';
import { exportToCSV } from '../hooks/exportToCSV';
import {api_base_url} from "../../../config";

const StoreCrawledCoupons = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(500);
  const [selectedSpider, setSelectedSpider] = useState("");
  const [spiderOptions, setSpiderOptions] = useState([]);
  const [isFilterOn, setIsFilterOn] = useState(true);
  const [filters, setFilters] = useState({ store_name: "", is_on_tr: "", is_new: "" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [storeFilter] = useState("true");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isTRModalOpen, setIsTRModalOpen] = useState(false);
  const [trAction, setTrAction] = useState(null); // 'add' ou 'remove'

  const availableFields = [
    { name: 'title', label: 'Título' },
    { name: 'description', label: 'Descrição' },
    { name: 'code', label: 'Código' },
    { name: 'url', label: 'URL' },
    { name: 'store_url', label: 'URL da Loja' },
    { name: 'store_name', label: 'Nome da Loja' },
    { name: 'spider', label: 'Spider' },
    { name: 'store_slug', label: 'SLUG' },
    { name: 'is_on_tr', label: 'TR?' },
    { name: 'last_time_seen', label: 'Visto por último' },
    { name: 'first_time_seen', label: 'Visto primeira vez' },
  ];
  const defaultSelectedFields = ['title', 'description', 'code', 'url'];

  const handleExportModal = () => {
    setIsExportModalOpen(true);
  };

  // Função para lidar com a exportação do modal
  const handleModalExport = (exportedData, selectedFields) => {
    console.log('Dados exportados:', exportedData);
    console.log('Campos selecionados:', selectedFields);
    
    setSnackbarMessage(`Exportação concluída! ${selectedFields.length} campos exportados.`);
    setOpenSnackbar(true);
  };

  // Função para exportação rápida (original)
  const handleExport = () => {
    const selectedData = filteredData.filter((row) => selectedRows.includes(row.id));
    exportToCSV(columns, selectedData.length > 0 ? selectedData : filteredData);
    
    setSnackbarMessage(`${selectedData.length > 0 ? selectedData.length : filteredData.length} registros exportados com sucesso!`);
    setOpenSnackbar(true);
  };

  // Função para abrir modal de ações TR
  const handleTRAction = (action) => {
    if (selectedRows.length === 0) {
      setSnackbarMessage('Selecione pelo menos um cupom para realizar esta ação.');
      setOpenSnackbar(true);
      return;
    }
    
    setTrAction(action);
    setIsTRModalOpen(true);
  };

  // Função para confirmar ação TR
  const handleTRConfirm = async (selectedData, action) => {
    try {
      const token = localStorage.getItem("access_token");
      const url = api_base_url + '/api/post-external/';

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          cupom_ids: selectedData.map(item => item.id)
        })
      });
      
      if (!response.ok) throw new Error('Erro na requisição');
      
      // Recarregar dados após ação
      refetch();
      
      const actionText = action === 'add' ? 'adicionados ao' : 'removidos do';
      setSnackbarMessage(`${selectedData.length} cupom(s) ${actionText} TR com sucesso!`);
      setOpenSnackbar(true);
      
      // Limpar seleção
      setSelectedRows([]);
      
    } catch (error) {
      console.error('Erro na ação TR:', error);
      throw error; // Re-throw para o modal capturar
    }
  };

  // Adicione callbacks para o modal
  handleTRConfirm.onSaveSuccess = (result) => {
    setSnackbarMessage(`${result.updated_count} cupom(s) salvos com sucesso!`);
    setOpenSnackbar(true);
    refetch(); // Atualiza a lista
  };

  handleTRConfirm.onError = (message) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  // Query para cupons de loja
  const { data = [], isLoading, refetch } = useGetCrawledCuponsQuery(
    { is_store: storeFilter },
    { skip: false }
  );

  const handleFiltersChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const ALL_SPIDERS = [
    "efacil_cupons",
    "kabum_cupons",
    "terabyteshop_cupons"
  ];

  useEffect(() => {
    if (Array.isArray(data)) {
      const uniqueSpiders = Array.from(new Set([
        ...ALL_SPIDERS,
        ...data.map(item => item.spider)
      ])).filter(Boolean).sort();
      setSpiderOptions(uniqueSpiders);
    } else {
      setSpiderOptions(ALL_SPIDERS);
    }
  }, [data]);

  const filteredData = useFilteredData(data, selectedSpider, filters);

  const handleSpiderChange = (event) => {
    setSelectedSpider(event.target.value);
  };

  const handleSyncComplete = (syncData) => {
    console.log('Sincronização concluída:', syncData);
  };  

  // Callback para quando a importação for concluída
  const handleImportComplete = (importData) => {
    setSnackbarMessage(`Importação concluída! Cupons processados: ${importData.total_processed || 'N/A'}`);
    setOpenSnackbar(true);
    // Recarrega os dados após importação
    refetch();
  };

  // Estatísticas específicas para cupons de loja
  const storeStats = {
    totalCoupons: data.length,
    filteredCoupons: filteredData.length,
    storeCoupons: data.filter(item => item.is_store === true).length,
    nonStoreCoupons: data.filter(item => item.is_store === false).length,
    spidersCount: spiderOptions.length,
    selectedCount: selectedRows.length
  };

  const columns = getColumns(isFilterOn, filters, handleFiltersChange, spiderOptions, selectedSpider, handleSpiderChange);

  return (
    <div className="flex flex-col space-y-4 p-4 min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Cabeçalho com gradiente específico para cupons de loja */}
      <div className="rounded-lg bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <Store className="h-8 w-8 text-white" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
              Cupons de Loja
            </h1>
            <p className="text-orange-100">
              Gerencie cupons específicos de lojas e estabelecimentos
            </p>
          </div>
        </div>
        
        {/* Stats Cards específicos para cupons de loja */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total de Cupons</p>
                <p className="text-2xl font-bold text-orange-600">{storeStats.totalCoupons}</p>
              </div>
              <ShoppingBag className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Cupons de Loja</p>
                <p className="text-2xl font-bold text-amber-600">{storeStats.storeCoupons}</p>
                <p className="text-xs text-gray-400">
                  {storeStats.totalCoupons > 0 ? ((storeStats.storeCoupons / storeStats.totalCoupons) * 100).toFixed(1) : 0}% do total
                </p>
              </div>
              <Store className="h-8 w-8 text-amber-500" />
            </div>
          </div>
          
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Cupons Filtrados</p>
                <p className="text-2xl font-bold text-yellow-600">{storeStats.filteredCoupons}</p>
              </div>
              <Filter className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Selecionados</p>
                <p className="text-2xl font-bold text-red-600">{storeStats.selectedCount}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Indicadores de distribuição */}
        <div className="mt-4 bg-white/20 backdrop-blur rounded-lg p-4">
          <h3 className="text-white font-medium mb-2">Distribuição de Cupons</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-400 rounded"></div>
              <span className="text-white text-sm">Cupons de Loja: {storeStats.storeCoupons}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-400 rounded"></div>
              <span className="text-white text-sm">Cupons Gerais: {storeStats.nonStoreCoupons}</span>
            </div>
          </div>
          
          {/* Barra de progresso visual */}
          <div className="mt-3 w-full bg-white/30 rounded-full h-2">
            <div 
              className="bg-green-400 h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${storeStats.totalCoupons > 0 ? (storeStats.storeCoupons / storeStats.totalCoupons) * 100 : 0}%` 
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
        severity="success"
      />

      {/* AppBar estilizado com componentes adicionais */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 h-2"></div>
        <AppBarWithFilters
          selectedSpider={selectedSpider}
          spiderOptions={spiderOptions}
          handleSpiderChange={handleSpiderChange}
          handleExport={handleExport}
          handleExportModal={handleExportModal}
          handleTRAction={handleTRAction}
          selectedRowsCount={selectedRows.length}
          showRunCommand={false}
          showAdvancedExport={true}
          showTRActions={true}
        >
          {/* Botão para importar cupons crawleados */}
          <ImportCouponsButton onImportComplete={handleImportComplete} />
          <SyncTrButton onSyncComplete={handleSyncComplete} />
          
          {/* Seção Zyte (opcional) */}
          <ZyteSection />
        </AppBarWithFilters>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-md overflow-visible">
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 h-2"></div>
        <TailwindDataTable
          data={filteredData}
          isLoading={isLoading}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSelectedRows={setSelectedRows}
          getRowId={(row) => row.id}
        />
      </div>
      
      {/* Rodapé */}
      <div className="text-center p-4 pt-12 text-sm text-gray-500">
        <div className="flex items-center justify-center space-x-4">
          <Store className="h-4 w-4" />
          <p>Cupons de Loja - Última atualização: {new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* Modais */}
      <ExportCouponsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={selectedRows.length > 0 ? filteredData.filter(row => selectedRows.includes(row.id)) : filteredData}
        availableFields={availableFields}
        defaultSelectedFields={defaultSelectedFields}
        onExport={handleModalExport}
      />

      <TRActionModal
        isOpen={isTRModalOpen}
        onClose={() => setIsTRModalOpen(false)}
        selectedData={filteredData.filter(row => selectedRows.includes(row.id))}
        action={trAction}
        onConfirm={handleTRConfirm}
        apiBaseUrl={api_base_url}
      />
    </div>
  );
};

export default StoreCrawledCoupons;